import { useState, FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import Swal from 'sweetalert2';
import { submitContact } from '@/api/client';
import ScrollReveal from '@/components/base/ScrollReveal';
import { useContent } from '@/contexts/ContentContext';

export default function ContactSection({ onOpenQuote }: { onOpenQuote?: () => void }) {
  const { t } = useTranslation('home');
  const { content } = useContent();

  const [formData, setFormData] = useState({
    fullName: '',
    phone: '',
    email: '',
    serviceType: '',
    address: '',
    preferredDate: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const serviceOptions = [
    'Deep Cleaning',
    'Regular Cleaning',
    'Light Cleaning',
    'Airbnb Cabin Cleaning',
  ];

  const companyPhone = content['company_phone']?.trim() || '(707) 623-6359';
  const companyEmail = content['company_email']?.trim() || '';
  const companyAddress = content['business_address']?.trim() || 'PO Box 115, Windsor, CA 95492';
  const weekdayHours = content['weekday_hours']?.trim() || '';

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();

    if (!formData.fullName || !formData.phone || !formData.email || !formData.serviceType) {
      Swal.fire({
        title: t('form_required'),
        text: t('form_required_msg'),
        icon: 'warning',
        confirmButtonColor: '#3DBFA0',
        confirmButtonText: t('admin_ok'),
      });
      return;
    }

    setIsSubmitting(true);
    try {
      const result = await submitContact({
        full_name: formData.fullName,
        phone: formData.phone,
        email: formData.email,
        service_type: formData.serviceType,
        property_address: formData.address || undefined,
        preferred_date: formData.preferredDate || undefined,
        message: formData.message || undefined,
      });

      if (result.success) {
        Swal.fire({
          title: t('form_success'),
          text: t('form_success_body'),
          icon: 'success',
          confirmButtonColor: '#3DBFA0',
          confirmButtonText: t('form_thanks'),
        });

        setFormData({
          fullName: '',
          phone: '',
          email: '',
          serviceType: '',
          address: '',
          preferredDate: '',
          message: '',
        });
      } else {
        Swal.fire({
          title: t('form_error'),
          text: result.message || t('form_error_msg'),
          icon: 'error',
          confirmButtonColor: '#3DBFA0',
          confirmButtonText: t('admin_ok'),
        });
      }
    } catch {
      Swal.fire({
        title: t('form_error'),
        text: t('form_error_msg'),
        icon: 'error',
        confirmButtonColor: '#3DBFA0',
        confirmButtonText: t('admin_ok'),
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="w-full py-16 md:py-20 bg-brand-light">
      <div className="px-4 md:px-6 lg:px-24 max-w-7xl mx-auto">
        {/* Section Header */}
        <ScrollReveal className="mb-10 md:mb-14 text-center">
          <div className="flex items-center justify-center gap-2 mb-3">
            <span className="w-2 h-2 rounded-full bg-brand-mint" />
            <span className="text-sm font-medium tracking-wider uppercase text-brand-mint">
              {t('contact_label')}
            </span>
          </div>
          <h2 className="text-2xl md:text-4xl font-bold text-brand-dark font-heading leading-tight mb-4">
            {t('contact_title')}
          </h2>
          <div className="w-10 h-1 bg-brand-mint rounded-full mx-auto" />
          <p className="mt-4 text-sm md:text-base text-brand-muted max-w-xl mx-auto">
            {t('contact_subtitle')}
          </p>
        </ScrollReveal>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-8 md:gap-12">
          {/* Contact Info */}
          <ScrollReveal className="lg:col-span-2 flex flex-col gap-6" direction="right" delay={100}>
            <div className="relative rounded-xl overflow-hidden">
              {/* Background image */}
              <div className="absolute inset-0">
                <img
                  src="https://readdy.ai/api/search-image?query=A%20professional%20female%20cleaner%20in%20teal%20uniform%20with%20gloves%20smiling%20while%20cleaning%20a%20bright%20modern%20kitchen%20counter%2C%20viewed%20from%20a%20slight%20side%20angle%20with%20face%20partially%20visible%20but%20not%20the%20main%20focus%2C%20warm%20natural%20daylight%2C%20premium%20residential%20cleaning%20photography%20with%20clean%20simple%20minimal%20background%20and%20soft%20teal%20color%20tones%2C%20single%20person%20only&width=600&height=800&seq=802&orientation=portrait"
                  alt="Cleaning professional"
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-b from-brand-dark/70 via-brand-dark/80 to-brand-dark/90" />
              </div>

              <div className="relative z-10 p-6 md:p-8 text-white">
                <h3 className="text-lg font-semibold font-heading mb-5">
                  {t('contact_info_title')}
                </h3>
                <div className="space-y-4">
                  <div className="flex items-start gap-3">
                    <div className="w-9 h-9 rounded-lg bg-white/15 flex items-center justify-center shrink-0">
                      <i className="ri-phone-line text-white" />
                    </div>
                    <div>
                      <p className="text-xs text-white/70">{t('contact_phone_label')}</p>
                      <p className="text-sm font-medium">
                    <a href="tel:+17076236359" className="hover:text-brand-mint transition-colors">
                      {companyPhone}
                    </a>
                  </p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-9 h-9 rounded-lg bg-white/15 flex items-center justify-center shrink-0">
                      <i className="ri-mail-line text-white" />
                    </div>
                    <div>
                      <p className="text-xs text-white/70">{t('contact_email_label')}</p>
                      <p className="text-sm font-medium">{companyEmail || 'info@completehomecarecleaning.com'}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-9 h-9 rounded-lg bg-white/15 flex items-center justify-center shrink-0">
                      <i className="ri-map-pin-line text-white" />
                    </div>
                    <div>
                      <p className="text-xs text-white/70">{t('contact_address_label')}</p>
                      <p className="text-sm font-medium">{companyAddress || 'Santa Rosa, CA'}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-9 h-9 rounded-lg bg-white/15 flex items-center justify-center shrink-0">
                      <i className="ri-time-line text-white" />
                    </div>
                    <div>
                      <p className="text-xs text-white/70">{t('contact_hours_label')}</p>
                      <p className="text-sm font-medium">{weekdayHours || '24 Hours'}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-brand-dark rounded-xl p-6 md:p-8 text-white">
              <h3 className="text-lg font-semibold font-heading mb-2">
                {t('contact_cta_title')}
              </h3>
              <p className="text-sm text-white/80 mb-4">
                {t('contact_cta_desc')}
              </p>
              <a
                href="tel:+17076236359"
                className="inline-flex items-center gap-2 px-5 py-2.5 bg-brand-mint text-white text-sm font-semibold rounded-full hover:bg-brand-mint/90 transition-colors whitespace-nowrap"
              >
                <i className="ri-phone-line" />
                {t('contact_call_now')}
              </a>
              <a
                href="https://wa.me/17076236359?text=Hello%2C%20please%2C%20you%20can%20call%20the%20number%207076236359%20for%20an%20estimate.%20Thank%20you%20Hello%2C%20thank%20you%20for%20contacting%20complet%20care%20Home%20cleaning%20through%20this%20page.%20From%20which%20city%20are%20you%20contacting%20us%3F%20We%20work%20with%20counties%20Marin%20Napa%20Sonoma%20and%20Lake%20Kanyon%20we%20specialize%20in%20cleaning%20services%2C%20from%20deep%20cleaning%20to%20light%20cleaning%20in%20homes%2C%20offices%2C%20restaurants%2C%20and%20wynery%20bars.%20Can%20you%20send%20me%20the%20square%20feet%20of%20the%20property%20please.%20Thank%20you"
                target="_blank"
                rel="noopener noreferrer"
                className="mt-2 w-full sm:w-auto inline-flex items-center justify-center gap-2 px-5 py-2.5 bg-[#25D366] text-white text-sm font-semibold rounded-full hover:bg-[#1ebe5a] transition-colors whitespace-nowrap"
              >
                <i className="ri-whatsapp-line" />
                WhatsApp
              </a>
              <button
                type="button"
                onClick={onOpenQuote}
                className="mt-3 w-full sm:w-auto inline-flex items-center justify-center gap-2 px-5 py-2.5 bg-white/10 text-white text-sm font-semibold rounded-full hover:bg-brand-mint transition-colors whitespace-nowrap cursor-pointer"
              >
                <i className="ri-file-list-3-line" />
                {t('quote_cta')}
              </button>
            </div>
          </ScrollReveal>

          {/* Form */}
          <ScrollReveal className="lg:col-span-3" direction="left" delay={200}>
            <form
              onSubmit={handleSubmit}
              className="bg-white rounded-xl p-6 md:p-8 shadow-sm"
            >
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                <div>
                  <label htmlFor="fullName" className="block text-sm font-medium text-brand-dark mb-1.5">
                    {t('form_fullName')} <span className="text-red-500">*</span>
                  </label>
                  <input
                    id="fullName"
                    name="fullName"
                    type="text"
                    value={formData.fullName}
                    onChange={handleChange}
                    placeholder={t('form_fullName_placeholder')}
                    className="w-full px-4 py-2.5 rounded-lg border border-gray-200 text-sm text-brand-dark placeholder:text-gray-400 focus:outline-none focus:border-brand-mint focus:ring-1 focus:ring-brand-mint transition-colors"
                    disabled={isSubmitting}
                  />
                </div>
                <div>
                  <label htmlFor="phone" className="block text-sm font-medium text-brand-dark mb-1.5">
                    {t('form_phone')} <span className="text-red-500">*</span>
                  </label>
                  <input
                    id="phone"
                    name="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={handleChange}
                    placeholder={t('form_phone_placeholder')}
                    className="w-full px-4 py-2.5 rounded-lg border border-gray-200 text-sm text-brand-dark placeholder:text-gray-400 focus:outline-none focus:border-brand-mint focus:ring-1 focus:ring-brand-mint transition-colors"
                    disabled={isSubmitting}
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-brand-dark mb-1.5">
                    {t('form_email')} <span className="text-red-500">*</span>
                  </label>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder={t('form_email_placeholder')}
                    className="w-full px-4 py-2.5 rounded-lg border border-gray-200 text-sm text-brand-dark placeholder:text-gray-400 focus:outline-none focus:border-brand-mint focus:ring-1 focus:ring-brand-mint transition-colors"
                    disabled={isSubmitting}
                  />
                </div>
                <div>
                  <label htmlFor="serviceType" className="block text-sm font-medium text-brand-dark mb-1.5">
                    {t('form_serviceType')} <span className="text-red-500">*</span>
                  </label>
                  <select
                    id="serviceType"
                    name="serviceType"
                    value={formData.serviceType}
                    onChange={handleChange}
                    className="w-full px-4 py-2.5 rounded-lg border border-gray-200 text-sm text-brand-dark bg-white focus:outline-none focus:border-brand-mint focus:ring-1 focus:ring-brand-mint transition-colors appearance-none"
                    disabled={isSubmitting}
                  >
                    <option value="">{t('form_serviceType_placeholder')}</option>
                    {serviceOptions.map((opt) => (
                      <option key={opt} value={opt}>
                        {opt}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                <div>
                  <label htmlFor="address" className="block text-sm font-medium text-brand-dark mb-1.5">
                    {t('form_address')}
                  </label>
                  <input
                    id="address"
                    name="address"
                    type="text"
                    value={formData.address}
                    onChange={handleChange}
                    placeholder={t('form_address_placeholder')}
                    className="w-full px-4 py-2.5 rounded-lg border border-gray-200 text-sm text-brand-dark placeholder:text-gray-400 focus:outline-none focus:border-brand-mint focus:ring-1 focus:ring-brand-mint transition-colors"
                    disabled={isSubmitting}
                  />
                </div>
                <div>
                  <label htmlFor="preferredDate" className="block text-sm font-medium text-brand-dark mb-1.5">
                    {t('form_date')}
                  </label>
                  <input
                    id="preferredDate"
                    name="preferredDate"
                    type="date"
                    value={formData.preferredDate}
                    onChange={handleChange}
                    className="w-full px-4 py-2.5 rounded-lg border border-gray-200 text-sm text-brand-dark placeholder:text-gray-400 focus:outline-none focus:border-brand-mint focus:ring-1 focus:ring-brand-mint transition-colors"
                    disabled={isSubmitting}
                  />
                </div>
              </div>

              <div className="mb-6">
                <label htmlFor="message" className="block text-sm font-medium text-brand-dark mb-1.5">
                  {t('form_message')}
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  rows={4}
                  maxLength={500}
                  placeholder={t('form_message_placeholder')}
                  className="w-full px-4 py-2.5 rounded-lg border border-gray-200 text-sm text-brand-dark placeholder:text-gray-400 focus:outline-none focus:border-brand-mint focus:ring-1 focus:ring-brand-mint transition-colors resize-none"
                  disabled={isSubmitting}
                />
                <p className="text-xs text-brand-muted mt-1 text-right">
                  {formData.message.length}/500
                </p>
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-8 py-3 bg-brand-mint text-white text-sm font-semibold rounded-lg hover:bg-brand-mint/90 transition-colors whitespace-nowrap cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {isSubmitting ? (
                  <>
                    <i className="ri-loader-4-line animate-spin" />
                    {t('form_sending')}
                  </>
                ) : (
                  <>
                    <i className="ri-send-plane-line" />
                    {t('form_submit')}
                  </>
                )}
              </button>
            </form>
          </ScrollReveal>
        </div>
      </div>
    </section>
  );
}